/**
 * Hook to check whether the store is currently accepting orders (i.e. launched).
 *
 * Fetches the public features config endpoint and reads the `acceptingOrders`
 * flag. While the flag is off, the app is Closed and customer routes render the
 * holding page (see CustomerRouteGate). Result is cached for 5 minutes to avoid
 * excessive requests.
 *
 * Fails closed: until the API responds — and if it errors — the status is
 * 'closed', so the public storefront is never briefly exposed before launch.
 */

import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';

interface FeaturesResponse {
  acceptingOrders?: boolean;
}

let cachedValue: boolean | null = null;
let cachedAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Upper bound on how long the gate may sit on the loading spinner. If
// /v1/config/features stalls (hung connection, no HTTP error), abort and fail
// closed to the holding page rather than trapping the whole customer surface
// behind an indefinite spinner.
const FETCH_TIMEOUT_MS = 8000;

export type AcceptingOrdersStatus = 'loading' | 'open' | 'closed';

function toStatus(open: boolean | null): AcceptingOrdersStatus {
  if (open === null) return 'loading';
  return open ? 'open' : 'closed';
}

/**
 * Returns the store's open/closed status. 'loading' until the API responds
 * (defaults to the cached value when still fresh).
 */
export function useAcceptingOrders(): AcceptingOrdersStatus {
  const [status, setStatus] = useState<AcceptingOrdersStatus>(
    toStatus(cachedValue),
  );

  useEffect(() => {
    const now = Date.now();
    if (cachedValue !== null && now - cachedAt < CACHE_TTL_MS) {
      setStatus(cachedValue ? 'open' : 'closed');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    async function check() {
      try {
        const data = await apiFetch<FeaturesResponse>(
          '/v1/config/features',
          { signal: controller.signal },
        );
        if (!cancelled) {
          const open = data.acceptingOrders === true;
          cachedValue = open;
          cachedAt = Date.now();
          setStatus(open ? 'open' : 'closed');
        }
      } catch {
        // Fail closed: never expose the storefront if we can't confirm it's
        // open — covers HTTP errors, network failures, and the timeout abort.
        if (!cancelled) {
          cachedValue = false;
          cachedAt = Date.now();
          setStatus('closed');
        }
      } finally {
        clearTimeout(timeout);
      }
    }

    check();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  return status;
}
